'use client'

// Landlord sidebar navigation component
// Provides navigation between dashboard sections with stats display
import { useState } from 'react'
import { Building2, FileText, CreditCard, CalendarCheck, Settings, LogOut, Menu, X, Home, Star, Bell } from 'lucide-react'
import { User } from '@/types'
import { LandlordTab } from '@/types/landlord'

interface LandlordSidebarProps {
  activeTab: LandlordTab
  onTabChange: (tab: LandlordTab) => void
  onLogout: () => void
  user: User
  stats: {
    totalProperties: number
    verifiedProperties: number
    pendingApplications: number
    totalRevenue: number
    netRevenue?: number
    totalCommission?: number
    pendingAmount?: number
  }
}

export default function LandlordSidebar({ 
  activeTab, 
  onTabChange, 
  onLogout, 
  user, 
  stats 
}: LandlordSidebarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const navigation = [
    {
      id: 'overview',
      name: 'Overview',
      icon: Home,
      description: 'Dashboard overview',
      count: null
    },
    {
      id: 'properties',
      name: 'Properties',
      icon: Building2,
      description: 'Manage your listings',
      count: stats.totalProperties
    },
    {
      id: 'applications',
      name: 'Applications',
      icon: FileText,
      description: 'Review tenant requests',
      count: stats.pendingApplications
    },
    {
      id: 'payments',
      name: 'Payments',
      icon: CreditCard,
      description: 'Track rent payments',
      count: null
    },
    {
      id: 'tenancies',
      name: 'Rent & Tenancies',
      icon: CalendarCheck,
      description: 'Rent periods & expiration',
      count: null
    },
    {
      id: 'reviews',
      name: 'Reviews',
      icon: Star,
      description: 'View property reviews',
      count: null
    },
    {
      id: 'profile',
      name: 'Profile',
      icon: Settings,
      description: 'Account settings',
      count: null
    },
    {
      id: 'settings',
      name: 'Notification Preferences',
      icon: Bell,
      description: 'Configure email & notification settings',
      count: null
    }
  ]

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    if (user?.kyc_data?.firstName && user?.kyc_data?.lastName) {
      return `${user.kyc_data.firstName} ${user.kyc_data.lastName}`
    }
    return user?.email || 'User'
  }

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2.5 rounded-xl bg-white shadow-lg border border-stone-200 hover:bg-stone-50 transition-colors"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-stone-700" />
          ) : (
            <Menu className="h-6 w-6 text-stone-700" />
          )}
        </button>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:inset-0 lg:w-72
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex-shrink-0 p-6 border-b border-stone-200 bg-gradient-to-br from-emerald-50 to-stone-50">
            <div className="flex items-center space-x-3 mb-4">
              {/* Logo */}
              <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-16 h-16 object-contain" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-stone-900 truncate">
                  Landlord<span className="text-emerald-600">NoAgent</span>
                </p>
                <p className="text-xs text-stone-500 truncate">Dashboard</p>
              </div>
            </div>
            
            {/* User Info */}
            <div className="bg-white rounded-xl p-4 border border-stone-200">
              <p className="text-sm font-semibold text-stone-900 truncate mb-1">
                {getUserDisplayName()}
              </p>
              <p className="text-xs text-stone-500 truncate">
                {user?.email}
              </p>
              {user?.is_verified && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-semibold text-emerald-700">Verified</span>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.id
              
              return (
                <button
                  key={item.id}
                  data-onboarding={
                    item.id === 'properties' ? 'properties-list' :
                    item.id === 'applications' ? 'applications-list' :
                    item.id === 'profile' ? 'profile-tab' : undefined
                  }
                  onClick={() => {
                    onTabChange(item.id as LandlordTab)
                    setIsMobileMenuOpen(false)
                  }}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 group
                    ${isActive 
                      ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-200 shadow-sm' 
                      : 'text-stone-700 hover:bg-stone-50 hover:text-stone-900 border-2 border-transparent'
                    }
                  `}
                >
                  <Icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-stone-400 group-hover:text-stone-600'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-semibold truncate ${isActive ? 'text-emerald-700' : ''}`}>
                        {item.name}
                      </p>
                      {item.count !== null && item.count > 0 && (
                        <span className={`
                          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold
                          ${isActive ? 'bg-emerald-600 text-white' : 'bg-stone-200 text-stone-700'}
                        `}>
                          {item.count}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${isActive ? 'text-emerald-600' : 'text-stone-500'}`}>
                      {item.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </nav>

          {/* Logout */}
          <div className="flex-shrink-0 p-4 border-t border-stone-200">
            <button
              onClick={onLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left text-red-600 hover:bg-red-50 transition-all duration-200 border-2 border-transparent hover:border-red-200"
            >
              <LogOut className="h-5 w-5" />
              <span className="text-sm font-semibold">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
